import { Client, Osdk } from "@osdk/client";
import { QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";
import { Dyad, getScoreHistoryForDyad } from "@unruly-osdk/sdk";

/**
 * Matches the shape returned by the Foundry `getScoreHistoryForDyad` function.
 */
export interface ScoreHistoryPoint {
  date: string;
  score: number;
  trajectory: string;
  assessmentId: string;
}

const keys = {
  all: ["dyads"] as const,
  byId: (dyadId: string) => [...keys.all, "id", dyadId] as const,
  scoreHistory: (dyadId: string) =>
    [...keys.all, "scoreHistory", dyadId] as const,
};

function DyadService(_queryClient: QueryClient, apiClient: Client) {
  const service = {
    getDyadById: async (
      dyadId: string,
    ): Promise<Osdk.Instance<Dyad> | null> => {
      const response = await apiClient(Dyad).where({ dyadId }).fetchPage();
      return response.data?.[0] || null;
    },

    useGetDyadById: (
      dyadId: string | undefined,
    ): UseQueryResult<Osdk.Instance<Dyad> | null> => {
      return useQuery({
        queryKey: keys.byId(dyadId ?? ""),
        queryFn: () => service.getDyadById(dyadId!),
        enabled: !!dyadId,
        staleTime: 5 * 60 * 1000,
      });
    },

    getScoreHistory: async (
      dyadId: string,
      limit?: number,
    ): Promise<ScoreHistoryPoint[]> => {
      const result = await apiClient(getScoreHistoryForDyad).executeFunction({
        dyadId,
        limit,
      });
      return result as ScoreHistoryPoint[];
    },

    useScoreHistory: (
      dyadId: string | undefined,
      enabled: boolean,
    ): UseQueryResult<ScoreHistoryPoint[]> => {
      return useQuery({
        queryKey: keys.scoreHistory(dyadId ?? ""),
        queryFn: () => service.getScoreHistory(dyadId!, 10),
        enabled: enabled && !!dyadId,
        staleTime: 10 * 60 * 1000,
      });
    },
  };

  return service;
}

export default DyadService;
